import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icon';
import { toast } from './ToastProvider';
import { apiFetch } from '../config/api';

const DAYS = [
  { key: 'mon', label: 'Poniedziałek' },
  { key: 'tue', label: 'Wtorek' },
  { key: 'wed', label: 'Środa' },
  { key: 'thu', label: 'Czwartek' },
  { key: 'fri', label: 'Piątek' },
  { key: 'sat', label: 'Sobota' },
  { key: 'sun', label: 'Niedziela' },
];

const HOURS = ['Zamknięte', '07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];

const DEFAULT_HOURS = {
  mon: { open: '09:00', close: '18:00', closed: false },
  tue: { open: '09:00', close: '18:00', closed: false },
  wed: { open: '09:00', close: '18:00', closed: false },
  thu: { open: '09:00', close: '18:00', closed: false },
  fri: { open: '09:00', close: '18:00', closed: false },
  sat: { open: '10:00', close: '16:00', closed: false },
  sun: { open: '09:00', close: '14:00', closed: true },
};

const SERVICE_TEMPLATES = [
  { name: 'Strzyżenie damskie', price: 80, duration: 45, category: 'Strzyżenie' },
  { name: 'Strzyżenie męskie', price: 50, duration: 30, category: 'Strzyżenie' },
  { name: 'Farbowanie', price: 150, duration: 90, category: 'Koloryzacja' },
  { name: 'Baleyage', price: 200, duration: 120, category: 'Koloryzacja' },
  { name: 'Keratyna', price: 250, duration: 120, category: 'Pielęgnacja' },
  { name: 'Modelowanie', price: 60, duration: 30, category: 'Stylizacja' },
  { name: 'Strzyżenie + stylizacja', price: 120, duration: 60, category: 'Strzyżenie' },
  { name: 'Golenie brody', price: 35, duration: 25, category: 'Barber' },
];

const SalonProfilePanel = ({ user }) => {
  const [tab, setTab] = useState('info');
  const [salonId, setSalonId] = useState(null);

  useEffect(() => {
    if (!user?.salonName) return;
    apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''))
      .then(salons => {
        const salon = salons.find(s => s.name === user.salonName);
        if (salon) setSalonId(salon.id);
      })
      .catch(() => {});
  }, [user?.salonName, user?.salonCity]);

  const [info, setInfo] = useState({
    salonName: user?.salonName || '',
    address: user?.salonAddress || '',
    city: user?.salonCity || '',
    phone: user?.phone || '',
    email: user?.email || '',
    description: '',
    instagram: '',
    facebook: '',
    photo: '',
  });

  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({ name: '', price: '', duration: '', category: 'Strzyżenie' });
  const [editIdx, setEditIdx] = useState(null);

  const load = useCallback(async () => {
    if (!salonId) return;
    try {
      const [hoursData, servicesData] = await Promise.all([
        apiFetch(`/salons/${salonId}/hours`),
        apiFetch(`/salons/${salonId}/services`),
      ]);
      
      const salonData = await apiFetch(`/salons/${salonId}`);
      if (salonData) {
        let formattedPhone = salonData.phone || '';
        if (formattedPhone) {
          let digits = formattedPhone.replace(/\D/g, '');
          if (digits.startsWith('48') && digits.length >= 11) digits = digits.substring(2);
          if (digits.length >= 9) {
            formattedPhone = `+48 ${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6,9)}`;
          }
        }
        
        setInfo(i => ({
          ...i,
          salonName: salonData.name || i.salonName,
          address: salonData.address || i.address,
          city: salonData.city || i.city,
          phone: formattedPhone || i.phone,
          email: salonData.email || i.email,
          description: salonData.description || i.description,
          instagram: salonData.instagram || i.instagram,
          facebook: salonData.facebook || i.facebook,
          photo: salonData.photo || i.photo,
        }));
      }
      
      if (hoursData?.length) {
        const mapped = {};
        hoursData.forEach(h => {
          mapped[h.day_key] = {
            open: h.open_time?.slice(0,5) || '09:00',
            close: h.close_time?.slice(0,5) || '18:00',
            closed: h.is_closed || false,
          };
        });
        setHours(h => ({ ...h, ...mapped }));
      }
      
      if (servicesData?.length) {
        setServices(servicesData.map(s => ({
          id: s.id,
          name: s.name,
          price: parseFloat(s.price),
          duration: s.duration,
          category: s.category || 'Strzyżenie',
        })));
      } else {
        setServices(SERVICE_TEMPLATES.slice(0, 5));
      }
    } catch { }
  }, [salonId]);

  useEffect(() => { load(); }, [load]);

  const handleInfoSave = async (e) => {
    e.preventDefault();
    if (!salonId) { toast.error('Salon nie znaleziony'); return; }
    try {
      await apiFetch(`/salons/${salonId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: info.salonName,
          address: info.address,
          city: info.city,
          phone: info.phone,
          email: info.email,
          description: info.description,
          instagram: info.instagram,
          facebook: info.facebook,
          photo: info.photo || null,
        }),
      });
      toast.success('Dane salonu zapisane!');
      setTimeout(() => { window.location.reload(); }, 1000);
    } catch (err) {
      toast.error(err.message || 'Błąd podczas zapisywania');
    }
  };

  const toggleDay = async (day) => {
    const next = { ...hours, [day]: { ...hours[day], closed: !hours[day].closed } };
    setHours(next);
    if (!salonId) return;
    try { await apiFetch(`/salons/${salonId}/hours`, { method: 'PUT', body: JSON.stringify({ hours: next }) }); } catch { }
  };

  const setHour = async (day, field, val) => {
    const next = { ...hours, [day]: { ...hours[day], [field]: val } };
    setHours(next);
    if (!salonId) return;
    try { await apiFetch(`/salons/${salonId}/hours`, { method: 'PUT', body: JSON.stringify({ hours: next }) }); } catch { }
  };

  const addService = async () => {
    if (!newService.name || !newService.price) return toast.warning('Wpisz nazwę i cenę');
    if (!salonId) return;
    try {
      await apiFetch(`/salons/${salonId}/services`, {
        method: 'POST',
        body: JSON.stringify({
          name: newService.name,
          price: parseFloat(newService.price),
          duration: parseInt(newService.duration) || 30,
          category: newService.category || 'Strzyżenie',
        }),
      });
      load();
      setNewService({ name: '', price: '', duration: '', category: 'Strzyżenie' });
      toast.success('Usługa dodana!');
    } catch { toast.error('Błąd podczas dodawania usługi'); }
  };

  const deleteService = async (idx) => {
    if (!window.confirm('Usunąć tę usługę?')) return;
    const service = services[idx];
    if (!salonId || !service?.id) return;
    try {
      await apiFetch(`/salons/${salonId}/services/${service.id}`, { method: 'DELETE' });
      load(); toast.info('Usunięto usługę');
    } catch { toast.error('Błąd podczas usuwania'); }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setNewService({ ...services[idx] });
  };

  const saveEdit = async () => {
    const service = services[editIdx];
    if (!salonId || !service?.id) return;
    try {
      await apiFetch(`/salons/${salonId}/services/${service.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: newService.name,
          price: parseFloat(newService.price),
          duration: parseInt(newService.duration) || 30,
          category: newService.category,
        }),
      });
      load();
      setEditIdx(null);
      setNewService({ name: '', price: '', duration: '', category: 'Strzyżenie' });
      toast.success('Usługa zaktualizowana!');
    } catch { toast.error('Błąd podczas aktualizacji'); }
  };

  const applyTemplate = async (tpl) => {
    if (services.some(s => s.name === tpl.name)) return toast.info('Ta usługa już istnieje');
    if (!salonId) return;
    try {
      await apiFetch(`/salons/${salonId}/services`, { method: 'POST', body: JSON.stringify(tpl) });
      load(); toast.success(`Dodano: ${tpl.name}`);
    } catch { toast.error('Błąd podczas dodawania'); }
  };

  const CATS = ['Strzyżenie', 'Koloryzacja', 'Pielęgnacja', 'Stylizacja', 'Barber', 'Inne'];

  return (
    <div className="spanel-wrapper">
      <div className="spanel-header">
        <div className="spanel-header-icon"><Icon name="salon" color="white" size="lg" /></div>
        <div>
          <h2>Profil salonu</h2>
          <p>{info.salonName || 'Twój salon'} · {info.city || '—'}</p>
        </div>
      </div>

      <div className="spanel-tabs">
        {[
          { id: 'info',     icon: 'info',     label: 'Dane salonu' },
          { id: 'hours',    icon: 'clock',    label: 'Godziny otwarcia' },
          { id: 'services', icon: 'service',  label: 'Usługi' },
        ].map(t => (
          <button key={t.id} className={`spanel-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} style={{ marginRight: '7px' }} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <form className="spanel-form" onSubmit={handleInfoSave}>
          <div className="spanel-section-title">Podstawowe informacje</div>
          <div className="spanel-grid">
            <div className="spanel-field">
              <label>Nazwa salonu</label>
              <input value={info.salonName} onChange={e => setInfo(i => ({ ...i, salonName: e.target.value }))} placeholder="np. Style Hair Rzeszów" />
            </div>
            <div className="spanel-field">
              <label>Miasto</label>
              <input value={info.city} onChange={e => setInfo(i => ({ ...i, city: e.target.value }))} placeholder="np. Rzeszów" />
            </div>
            <div className="spanel-field spanel-field-wide">
              <label>Adres</label>
              <input value={info.address} onChange={e => setInfo(i => ({ ...i, address: e.target.value }))} placeholder="np. ul. 3 Maja 12" />
            </div>
            <div className="spanel-field">
              <label>Telefon *</label>
              <input 
                type="tel" 
                value={info.phone || '+48 '} 
                onChange={e => {
                  let val = e.target.value;
                  if (!val.includes('+48')) val = '+48 ' + val;
                  
                  let digits = val.replace(/\D/g, '');
                  if (digits.startsWith('48')) digits = digits.substring(2);
                  digits = digits.substring(0, 9);
                  
                  let formatted = '+48';
                  if (digits.length > 0) formatted += ' ' + digits.substring(0, 3);
                  if (digits.length > 3) formatted += ' ' + digits.substring(3, 6);
                  if (digits.length > 6) formatted += ' ' + digits.substring(6, 9);
                  
                  setInfo(i => ({ ...i, phone: formatted === '+48' ? '+48 ' : formatted }));
                }} 
                placeholder="+48 123 456 789"
                pattern="^\+48\s\d{3}\s\d{3}\s\d{3}$"
                title="Wymagany format: +48 XXX XXX XXX"
                required
              />
              <div className="spanel-hint" style={{fontSize:'0.85em', color:'#6b7280', marginTop:'4px'}}>
                Wpisz 9 cyfr, a format ułoży się sam.
              </div>
            </div>
            <div className="spanel-field">
              <label>E-mail kontaktowy</label>
              <input type="email" value={info.email} onChange={e => setInfo(i => ({ ...i, email: e.target.value }))} />
            </div>
          </div>

          <div className="spanel-section-title" style={{ marginTop: '24px' }}>Zdjęcie salonu</div>
          <div className="spanel-grid">
            <div className="spanel-field spanel-field-wide">
              <label>Zdjęcie główne <span className="spanel-hint">(wyświetlane w rezerwacji)</span></label>
              {info.photo && (
                <div style={{marginBottom:'12px'}}>
                  <img src={info.photo} alt="Salon" style={{maxWidth:'200px', maxHeight:'200px', objectFit:'cover', borderRadius:'8px', border:'2px solid #e5e7eb'}} />
                  <button 
                    type="button" 
                    onClick={() => setInfo(i => ({...i, photo:''}))}
                    style={{display:'block', marginTop:'8px', padding:'6px 12px', fontSize:'0.85em', background:'#ef4444', color:'white', border:'none', borderRadius:'6px', cursor:'pointer'}}
                  >
                    Usuń zdjęcie
                  </button>
                </div>
              )}
              <input 
                type="file" 
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setInfo(i => ({...i, photo: reader.result}));
                  reader.readAsDataURL(file);
                }}
                style={{fontSize:'0.9em'}}
              />
              <div className="spanel-hint" style={{marginTop:'6px'}}>Rekomendowane: 400x400px, max 2MB</div>
            </div>
          </div>

          <div className="spanel-section-title" style={{ marginTop: '24px' }}>Opis i media społecznościowe</div>
          <div className="spanel-grid">
            <div className="spanel-field spanel-field-wide">
              <label>Opis salonu <span className="spanel-hint">(widoczny w wyszukiwarce)</span></label>
              <textarea
                value={info.description}
                onChange={e => setInfo(i => ({ ...i, description: e.target.value }))}
                placeholder="Opisz swój salon, specjalizacje, atmosferę..."
                rows={3}
              />
            </div>
            <div className="spanel-field">
              <label><Icon name="instagram" style={{ marginRight: '6px' }} />Instagram</label>
              <input value={info.instagram} onChange={e => setInfo(i => ({ ...i, instagram: e.target.value }))} placeholder="@twojsalon" />
            </div>
            <div className="spanel-field">
              <label><Icon name="facebook" style={{ marginRight: '6px' }} />Facebook</label>
              <input value={info.facebook} onChange={e => setInfo(i => ({ ...i, facebook: e.target.value }))} placeholder="fb.com/twojsalon" />
            </div>
          </div>

          <div className="spanel-actions">
            <button type="submit" className="spanel-save-btn">
              <Icon name="check" style={{ marginRight: '8px' }} />Zapisz dane salonu
            </button>
          </div>
        </form>
      )}

      {tab === 'hours' && (
        <div className="spanel-hours">
          <div className="spanel-section-title">Godziny otwarcia</div>
          <p className="spanel-hint-p">Kliknij przełącznik aby zamknąć salon w dany dzień. Zmiany zapisują się automatycznie.</p>
          <div className="spanel-hours-list">
            {DAYS.map(({ key, label }) => {
              const d = hours[key] || DEFAULT_HOURS[key];
              return (
                <div key={key} className={`spanel-day-row ${d.closed ? 'closed' : ''}`}>
                  <div className="spanel-day-name">{label}</div>
                  <button
                    className={`spanel-toggle ${d.closed ? '' : 'on'}`}
                    onClick={() => toggleDay(key)}
                    type="button"
                  >
                    <span className="spanel-toggle-knob" />
                  </button>
                  {d.closed ? (
                    <span className="spanel-closed-label">Zamknięte</span>
                  ) : (
                    <div className="spanel-hours-selects">
                      <select value={d.open} onChange={e => setHour(key, 'open', e.target.value)}>
                        {HOURS.filter(h => h !== 'Zamknięte').map(h => <option key={h}>{h}</option>)}
                      </select>
                      <span className="spanel-dash">—</span>
                      <select value={d.close} onChange={e => setHour(key, 'close', e.target.value)}>
                        {HOURS.filter(h => h !== 'Zamknięte').map(h => <option key={h}>{h}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="spanel-hours-preview">
            <div className="spanel-section-title">Podgląd tygodnia</div>
            <div className="spanel-hours-preview-grid">
              {DAYS.map(({ key, label }) => {
                const d = hours[key] || DEFAULT_HOURS[key];
                return (
                  <div key={key} className={`spanel-preview-day ${d.closed ? 'closed' : ''}`}>
                    <span className="spanel-preview-label">{label.slice(0, 2)}</span>
                    <span className="spanel-preview-time">{d.closed ? '—' : `${d.open}-${d.close}`}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === 'services' && (
        <div className="spanel-services">
          <div className="spanel-section-title">{editIdx !== null ? 'Edytuj usługę' : 'Dodaj usługę'}</div>
          <div className="spanel-service-form">
            <div className="spanel-field">
              <label>Nazwa usługi</label>
              <input value={newService.name} onChange={e => setNewService(s => ({ ...s, name: e.target.value }))} placeholder="np. Strzyżenie damskie" />
            </div>
            <div className="spanel-field">
              <label>Cena (zł)</label>
              <input type="number" min="0" value={newService.price} onChange={e => setNewService(s => ({ ...s, price: e.target.value }))} placeholder="80" />
            </div>
            <div className="spanel-field">
              <label>Czas (min)</label>
              <input type="number" min="0" step="5" value={newService.duration} onChange={e => setNewService(s => ({ ...s, duration: e.target.value }))} placeholder="45" />
            </div>
            <div className="spanel-field">
              <label>Kategoria</label>
              <select value={newService.category} onChange={e => setNewService(s => ({ ...s, category: e.target.value }))}>
                {CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="spanel-service-form-btns">
              {editIdx !== null ? (
                <>
                  <button className="spanel-save-btn" onClick={saveEdit} type="button">
                    <Icon name="check" style={{ marginRight: '7px' }} />Zapisz
                  </button>
                  <button className="spanel-cancel-btn" onClick={() => { setEditIdx(null); setNewService({ name: '', price: '', duration: '', category: 'Strzyżenie' }); }} type="button">
                    Anuluj
                  </button>
                </>
              ) : (
                <button className="spanel-save-btn" onClick={addService} type="button">
                  <Icon name="add" style={{ marginRight: '7px' }} />Dodaj usługę
                </button>
              )}
            </div>
          </div>

          <div className="spanel-section-title" style={{ marginTop: '24px' }}>
            Szybkie dodawanie <span className="spanel-hint">— gotowe szablony</span>
          </div>
          <div className="spanel-templates">
            {SERVICE_TEMPLATES.map(tpl => (
              <button key={tpl.name} className="spanel-template-btn" onClick={() => applyTemplate(tpl)} type="button">
                <Icon name="add" style={{ marginRight: '5px', fontSize: '0.8em' }} />{tpl.name}
                <span className="spanel-template-price">{tpl.price} zł</span>
              </button>
            ))}
          </div>

          <div className="spanel-section-title" style={{ marginTop: '24px' }}>
            Aktualne usługi <span className="spanel-hint">({services.length})</span>
          </div>
          {services.length === 0 ? (
            <div className="b2b-empty-state">
              <div className="b2b-empty-icon"><Icon name="service" color="#d1d5db" size="2x" /></div>
              <h3>Brak usług</h3>
              <p>Dodaj usługi które oferuje Twój salon</p>
            </div>
          ) : (
            <div className="spanel-service-list">
              {Object.entries(services.reduce((acc, s) => {
                acc[s.category] = [...(acc[s.category] || []), s];
                return acc;
              }, {})).map(([cat, svcs]) => (
                <div key={cat} className="spanel-service-group">
                  <div className="spanel-service-cat">{cat}</div>
                  {svcs.map((s, i) => {
                    const realIdx = services.findIndex(x => x === s || x.id === s.id);
                    return (
                      <div key={i} className="spanel-service-row">
                        <div className="spanel-service-info">
                          <span className="spanel-service-name">{s.name}</span>
                          <span className="spanel-service-meta">
                            <Icon name="clock" style={{ marginRight: '4px', fontSize: '0.85em' }} />{s.duration} min
                          </span>
                        </div>
                        <div className="spanel-service-right">
                          <span className="spanel-service-price">{s.price} zł</span>
                          <button className="spanel-icon-btn edit" onClick={() => startEdit(realIdx)} type="button">
                            <Icon name="edit" />
                          </button>
                          <button className="spanel-icon-btn delete" onClick={() => deleteService(realIdx)} type="button">
                            <Icon name="trash" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalonProfilePanel;